import React, { useState } from 'react';
import { theme, alpha } from '../../theme';

interface RoleSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRoleSelected: (role: 'PLAYER' | 'VENUE_OWNER') => Promise<void>;
}

const RoleSelectionModal: React.FC<RoleSelectionModalProps> = ({
  isOpen,
  onClose,
  onRoleSelected
}) => {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleRoleClick = async (role: 'PLAYER' | 'VENUE_OWNER') => {
    setLoading(true);
    try {
      await onRoleSelected(role);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error selecting role:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: '18px',
        padding: '48px',
        maxWidth: '600px',
        width: '100%',
        position: 'relative',
        border: `2px solid ${theme.colors.border}`,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={loading}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'none',
            border: 'none',
            color: theme.colors.textSecondary,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '28px',
            padding: '4px',
            opacity: loading ? 0.5 : 1,
            transition: 'all 0.2s ease'
          }}
        >
          ✕
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '32px',
            fontWeight: 700,
            color: theme.colors.text,
            margin: '0 0 12px 0',
            fontFamily: theme.fonts.heading
          }}>
            ¿Cómo querés usar Futbolero?
          </h2>
          <p style={{
            color: theme.colors.textSecondary,
            fontSize: '16px',
            margin: 0,
            fontFamily: theme.fonts.body
          }}>
            Podés cambiarlo después desde tu perfil
          </p>
        </div>

        {/* Role Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '24px'
        }}>
          {/* Player Card */}
          <div
            onClick={() => !loading && handleRoleClick('PLAYER')}
            style={{
              padding: '32px',
              backgroundColor: theme.colors.card,
              border: `2px solid ${theme.colors.border}`,
              borderRadius: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'center',
              opacity: loading ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.borderColor = theme.colors.primary;
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 8px 24px ${alpha('primary', '40')}`;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.borderColor = theme.colors.border;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>⚽</div>
            <h3 style={{
              fontSize: '24px',
              fontWeight: 700,
              color: theme.colors.primary,
              margin: '0 0 12px 0',
              fontFamily: theme.fonts.heading,
              letterSpacing: '0.5px'
            }}>
              SOY JUGADOR
            </h3>
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              margin: 0,
              lineHeight: 1.5,
              fontFamily: theme.fonts.body
            }}>
              Quiero crear partidos, unirme a partidos y reservar canchas para jugar
            </p>
          </div>

          {/* Owner Card */}
          <div
            onClick={() => !loading && handleRoleClick('VENUE_OWNER')}
            style={{
              padding: '32px',
              backgroundColor: theme.colors.card,
              border: `2px solid ${theme.colors.border}`,
              borderRadius: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'center',
              opacity: loading ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.borderColor = theme.colors.primary;
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 8px 24px ${alpha('primary', '40')}`;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.borderColor = theme.colors.border;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🏟️</div>
            <h3 style={{
              fontSize: '24px',
              fontWeight: 700,
              color: theme.colors.primary,
              margin: '0 0 12px 0',
              fontFamily: theme.fonts.heading,
              letterSpacing: '0.5px'
            }}>
              SOY PROPIETARIO
            </h3>
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              margin: 0,
              lineHeight: 1.5,
              fontFamily: theme.fonts.body
            }}>
              Tengo canchas o un complejo deportivo y quiero gestionarlo
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleSelectionModal;
